import { Plural, Trans, useLingui } from "@lingui/react/macro";

import { HeartCoreNumberOfMeasurements } from "../../../api";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";

const SEGMENT_COLORS = [
  "bg-recency-1",
  "bg-recency-2",
  "bg-recency-3",
  "bg-recency-4",
];

/**
 * Measurement volume for the patient profile, as one stacked bar split into
 * age buckets, most recent first — leftmost segment, first ramp step.
 *
 * The API reports *cumulative* windows — the 12-month count contains the
 * 3-month one — so rendering the raw counts side by side reads as four
 * unrelated numbers. Subtracting adjacent windows turns them into disjoint
 * buckets whose widths sum to the yearly total, which is what makes the
 * measurement cadence legible at a glance.
 */
export function MeasurementsRecency(props: {
  windows: HeartCoreNumberOfMeasurements[];
}) {
  const { t } = useLingui();
  const buckets = toBuckets(props.windows);
  if (buckets.length === 0) {
    return null;
  }

  const span = buckets[buckets.length - 1].to;
  const total = buckets.reduce((sum, b) => sum + b.count, 0);

  if (total === 0) {
    return (
      <span className="text-muted-foreground text-xs">
        <Trans>No measurements in the last {span} months</Trans>
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="text-muted-foreground text-2xs font-semibold tracking-wide uppercase">
          <Trans>Measurements</Trans>
        </span>
        <span className="text-lg leading-none font-bold tabular-nums">
          {total}
        </span>
        <span className="text-muted-foreground text-2xs">
          <Trans>in last {span} months</Trans>
        </span>
      </div>

      {/* Widths carry the counts; the labels below repeat them as text, so the
          bar itself is decorative for assistive tech. */}
      <div
        aria-hidden="true"
        className="flex h-2 w-60 max-w-full gap-0.5 overflow-hidden rounded-sm"
      >
        {/* Empty buckets are skipped inside the map, not filtered out before
            it, so `index` stays the bucket's own position and the segment
            colors keep matching the legend dots below. */}
        {buckets.map((bucket, index) =>
          bucket.count === 0 ? null : (
            <Tooltip
              key={bucket.to}
              content={
                <>
                  <Plural
                    value={bucket.count}
                    one="# measurement"
                    other="# measurements"
                  />
                  {` · ${t`${bucket.from}–${bucket.to} months ago`} · ${monthRange(bucket.start, bucket.end)}`}
                </>
              }
            >
              <div
                className={`min-w-1.5 ${SEGMENT_COLORS[index] ?? SEGMENT_COLORS[SEGMENT_COLORS.length - 1]}`}
                style={{ flexGrow: bucket.count, flexBasis: 0 }}
              />
            </Tooltip>
          ),
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs">
        {buckets.map((bucket, index) => (
          <span
            key={bucket.to}
            className={`flex items-center gap-1 ${bucket.count === 0 ? "opacity-50" : ""}`}
          >
            <span
              className={`size-1.5 shrink-0 rounded-full ${SEGMENT_COLORS[index] ?? SEGMENT_COLORS[SEGMENT_COLORS.length - 1]}`}
            />
            <span className="text-muted-foreground">
              {bucket.from}–{bucket.to}m
            </span>
            <span className="font-semibold tabular-nums">{bucket.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** One disjoint age bucket: measurements taken between `from` and `to` months ago. */
interface Bucket {
  from: number;
  to: number;
  count: number;
  /** Older edge of the bucket. */
  start: string;
  /** Newer edge of the bucket. */
  end: string;
}

/**
 * Cumulative windows → disjoint buckets, shortest window first. Counts are
 * clamped at zero so a non-monotonic response degrades to an empty segment
 * instead of a negative width.
 */
function toBuckets(windows: HeartCoreNumberOfMeasurements[]): Bucket[] {
  const sorted = [...windows].sort((a, b) => a.report_length - b.report_length);

  return sorted.map((window, index) => {
    const newer = sorted[index - 1];
    return {
      from: newer?.report_length ?? 0,
      to: window.report_length,
      count: Math.max(
        0,
        window.number_of_measurements - (newer?.number_of_measurements ?? 0),
      ),
      start: window.report_start_date,
      end: newer?.report_start_date ?? window.report_end_date,
    };
  });
}

/** e.g. "Apr – Jul 2026", collapsing the year when both edges share it. */
function monthRange(start: string, end: string): string {
  const from = new Date(start);
  const to = new Date(end);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return "";

  const month = (date: Date, withYear: boolean) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      ...(withYear ? { year: "numeric" } : {}),
    });

  const sameYear = from.getFullYear() === to.getFullYear();
  return `${month(from, !sameYear)} – ${month(to, true)}`;
}
